import { useNavigate } from "react-router";
import {
  MotifIcon,
  MotifTruncate,
  MotifTooltip,
  MotifChip,
} from "@ey-xd/motif-react";
import {
  IconoirArrowLeft,
  IconoirCheckCircle,
  IconoirOpenNewWindow,
} from "@ey-xd/motif-icon";
import Breadcrumb from "../../../../components/Breadcrumb";
import InfoTooltip from "../../../../components/InfoTooltip";
import {
  getDurationType,
  calculateTotalCost,
  calculatePeriodTotalCost,
  getTotalOrderAmountVariable,
} from "../../OrderExtensionHelper";
import {
  skuIDInfo,
  unitCostInfo,
  unitPerPeriodInfo,
  numberOfPeriodsInfo,
  subTotalInfo,
  fixedCostInfo,
  variableCostInfo,
  extensionCostInfo,
  variableTotalInfo,
} from "../../../../utils/skuTable";
import "./OrderPlaced.scss";
import {
  getCostIntervalForCostMetric,
  getUnitsUsedForPeriod,
} from "../../../Checkout/CheckoutHelper";
import {
  SKU_EXTENSION_PLACED_TITLE,
  SKU_EXTENSION_PLACED_DESCRIPTION,
  ORDER_EXTENSION_PLACED_TITLE,
  ORDER_EXTENSION_PLACED_DESCRIPTION,
} from "../../../../Constant";

export const OrderPlaced = ({
  orderData,
  isSKUExtensionConfirmation,
}: {
  orderData: any;
  isSKUExtensionConfirmation: boolean;
}) => {
  const nav = useNavigate();

  const getDuration = (sku: any) => {
    if (sku.CostIntervalCd == "onetime") {
      return (
        <div className="table-cell">
          <div>--</div>
          <div className="sku-number">Not Applicable</div>
        </div>
      );
    } else {
      return (
        <>
          <div>{sku.OrderInterval}</div>
          <div className="sku-number">
            {getDurationType(sku.CostIntervalCd)}
          </div>
        </>
      );
    }
  };

  const zeroCostWithDecimal = () => {
    const totalCost = 0;
    return totalCost.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  };
  const breadcrumbItems = [
    {
      label: "External Submission",
      value: "Home",
      elementKey: "submission",
      text: "Home",
      url: `/`,
      icon: <MotifIcon src={IconoirArrowLeft} />,
    },
    {
      label: "External Submission",
      value: "Checkout",
      elementKey: "submission",
      text: isSKUExtensionConfirmation
        ? "SKU Extension Confirmation"
        : "Order Extension Confirmation",
      url: `/order-extension`,
      disabled: true,
      icon: <MotifIcon src={IconoirArrowLeft} />,
    },
  ];

  const totalVariable = getTotalOrderAmountVariable(orderData?.Details || []);
  const totalVariableWihoutSymbol = totalVariable.replace("$", "");

  return (
    <div className="sku-order-confirmation-detail-wrapper">
      <section
        className={`sku-order-confirmation-content-header sku-order-confirmation-header-banner-lg`}
      >
        <div>
          <Breadcrumb
            items={breadcrumbItems}
            ariaLabel="Breadcrumb navigation"
          />
        </div>
        <div className="header-grid">
          <div className="sku-order-confirmation-grid-icon">
            <MotifIcon src={IconoirCheckCircle} fill="#26A04B" size="24" />
          </div>
          <div>
            <div className="sku-order-confirmation-title">
              {isSKUExtensionConfirmation
                ? SKU_EXTENSION_PLACED_TITLE
                : ORDER_EXTENSION_PLACED_TITLE}
            </div>
            <div className="sku-order-confirmation-description">
              {isSKUExtensionConfirmation
                ? SKU_EXTENSION_PLACED_DESCRIPTION
                : ORDER_EXTENSION_PLACED_DESCRIPTION}
            </div>
            {orderData.CRMSyncFailureStatus != null &&
              orderData.CRMSyncFailureStatus >= 1 && (
                <div className="success-grid-order-description order-expected-description">
                  We’re currently unable to sync it with the CRM, but you can
                  retry the sync later from{" "}
                  <span
                    className="order-expected-link-value"
                    onClick={() => nav("/my-workspace/my-orders")}
                  >
                    My Orders
                  </span>
                  .
                </div>
              )}
          </div>
        </div>
      </section>
      <div className="main-container">
        <div className="sku-confirmation-content-section">
          <div>
            <div className="header-name">{orderData?.OrderDescription}</div>
            <div className="header-value">{orderData?.OrderId}</div>
          </div>
          <div>
            <div className="header-name">Track extension in pipeline</div>
            <div
              className="header-value order-link-value"
              onClick={() => nav("/order-pipeline/orders")}
            >
              Order pipeline
              <MotifIcon
                iconFunction={IconoirOpenNewWindow}
                size="13"
                fill="#55A6FC"
                className="link-icon"
              />
            </div>
          </div>
          <div>
            <div className="header-name text-align-right">Total cost</div>
            <div className="header-value text-align-right total-cost">
              {orderData?.TotalCost.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              })}
            </div>
            <div className="header-sub-value text-align-right">
              {Number(totalVariableWihoutSymbol) > 0 && (
                <div className="cost-estimate-value-support">
                  <div className="variable-cost-info-grid">
                    <div>
                      {getTotalOrderAmountVariable(orderData.Details)} Variable
                      cost
                    </div>
                    <InfoTooltip
                      description={variableTotalInfo()}
                      info={true}
                      id={`variable-cost-info-tooltip`}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div>
          {orderData?.Details?.map((detail: any) => (
            <div
              className="sku-confirmation-content-details"
              key={detail?.ProductId}
            >
              <div className="sku-list">
                <div className="sku-list-header">{detail?.ProductName}</div>
                <div className="sku-list-table">
                  <table className="motif-table-standard">
                    <thead>
                      <tr>
                        <th style={{ minWidth: "380px" }}>
                          <span className="label-with-info">
                            <span>SKU name/SKU ID</span>
                            <InfoTooltip
                              description={skuIDInfo()}
                              id={`sku-id-info-tooltip`}
                            />
                          </span>
                        </th>
                        <th style={{ minWidth: "160px" }}>Engagement code</th>
                        <th
                          style={{ minWidth: "140px" }}
                          className="text-right-align"
                        >
                          <span className="label-with-info">
                            <span>Unit Cost</span>
                            <InfoTooltip
                              description={unitCostInfo()}
                              id={`unit-cost-info-tooltip`}
                            />
                          </span>
                        </th>
                        <th style={{ minWidth: "150px", paddingRight: "0" }}>
                          <span className="label-with-info">
                            <span>Units per period</span>
                            <InfoTooltip
                              description={unitPerPeriodInfo()}
                              id={`units-per-period-info-tooltip`}
                            />
                          </span>
                        </th>
                        <th style={{ minWidth: "150px", paddingRight: "0" }}>
                          <span className="label-with-info">
                            <span>Number of periods</span>
                            <InfoTooltip
                              description={numberOfPeriodsInfo()}
                              id={`number-of-periods-info-tooltip`}
                            />
                          </span>
                        </th>
                        <th style={{ minWidth: "160px" }}>
                          <span className="label-with-info">
                            <span>Subtotal</span>
                            <InfoTooltip
                              description={subTotalInfo()}
                              id={`sub-total-info-tooltip`}
                            />
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail?.SkuDetails.map((sku: any, index: number) => {
                        if (sku?.SkuRelation?.length === 0) {
                          return (
                            <tr key={index}>
                              <td>
                                <div className="table-content-bold">
                                  <MotifTruncate maxLines={1} maxWidth={200}>
                                    {sku.SkuName}
                                  </MotifTruncate>
                                </div>
                                <div className="sku-number">
                                  <span>{sku.SkuId}</span>

                                  <MotifTooltip
                                    contentClassName="tooltip-content"
                                    placement="top"
                                    hideCloseButton
                                    flip
                                    offset={[0, 8]}
                                    allowHover
                                    allowClick={true}
                                    trigger={
                                      <MotifChip
                                        className={`sku-badge ${sku.SkuType === "Fixed" ? "fixed" : sku.SkuType === "Variable" ? "variable" : "extension"}`}
                                        onClick={() => {}}
                                        disabled={false}
                                      >
                                        {sku?.SkuType}
                                      </MotifChip>
                                    }
                                    variant="default"
                                  >
                                    <div className="label-info-icon-text">
                                      {sku?.SkuType == "Fixed"
                                        ? fixedCostInfo()
                                        : sku.SkuType === "Variable"
                                          ? variableCostInfo()
                                          : extensionCostInfo()}
                                    </div>
                                  </MotifTooltip>
                                </div>
                              </td>
                              <td>
                                <div className="table-content-bold">
                                  {sku?.EngagementId ? sku.EngagementId : "--"}
                                </div>
                                <div className="table-content-normal">
                                  {sku?.EngagementName ? (
                                    <MotifTruncate maxLines={1} maxWidth={120}>
                                      {sku?.EngagementName}
                                    </MotifTruncate>
                                  ) : (
                                    "--"
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="text-right-align table-content-bold">
                                  {sku?.CostMetricCost.toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                    maximumFractionDigits: 2,
                                    minimumFractionDigits: 2,
                                  })}
                                </div>
                                <div className="text-right-align table-content-normal">
                                  {getCostIntervalForCostMetric(
                                    sku.CostIntervalCd,
                                    sku.CostMetricCd,
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="table-content-bold">
                                  {sku?.OrderSkuQuantity?.toString() ?? ""}
                                </div>
                                <div className="table-content-normal">
                                  {getUnitsUsedForPeriod(
                                    sku.CostIntervalCd,
                                    sku.CostMetricCd,
                                    sku?.OrderSkuQuantity,
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="table-content-bold">
                                  {sku?.IsExtension ? sku.OrderSkuIntervals : 0}
                                </div>
                                <div className="table-content-normal">
                                  {getDuration(sku)}
                                </div>
                              </td>
                              <td>
                                <div className="text-right-align table-content-bold sub-total-cost">
                                  {sku?.IsExtension
                                    ? calculateTotalCost(sku)
                                    : zeroCostWithDecimal()}
                                </div>
                                <div className="text-right-align table-content-normal">
                                  {sku?.IsExtension
                                    ? calculatePeriodTotalCost(sku)
                                    : zeroCostWithDecimal()}
                                  {`/${sku.CostIntervalCd}`}
                                </div>
                              </td>
                            </tr>
                          );
                        } else {
                          return (
                            <tr key={index}>
                              <td>
                                <ul className="tree">
                                  <li className="tree-trigger">
                                    <div className="table-cell">
                                      <div className="table-content-bold">
                                        <MotifTruncate
                                          maxLines={1}
                                          maxWidth={200}
                                        >
                                          {sku.SkuName}
                                        </MotifTruncate>
                                      </div>
                                      <div
                                        className="sku-number"
                                        style={{
                                          display: "flex",
                                          gap: "6px",
                                          alignItems: "center",
                                        }}
                                      >
                                        <span>{sku.SkuId}</span>

                                        <MotifTooltip
                                          contentClassName="tooltip-content"
                                          placement="top"
                                          hideCloseButton
                                          flip
                                          offset={[0, 8]}
                                          allowHover
                                          allowClick={true}
                                          trigger={
                                            <MotifChip
                                              className={`sku-badge ${sku.SkuType === "Fixed" ? "fixed" : sku.SkuType === "Variable" ? "variable" : "extension"}`}
                                              onClick={() => {}}
                                              disabled={false}
                                            >
                                              {sku?.SkuType}
                                            </MotifChip>
                                          }
                                          variant="default"
                                        >
                                          <div className="label-info-icon-text">
                                            {sku?.SkuType == "Fixed"
                                              ? fixedCostInfo()
                                              : sku.SkuType === "Variable"
                                                ? variableCostInfo()
                                                : extensionCostInfo()}
                                          </div>
                                        </MotifTooltip>
                                      </div>
                                    </div>
                                  </li>
                                  <ul>
                                    {sku?.SkuRelation?.map(
                                      (skuRelation: any) => {
                                        return (
                                          <li>
                                            <div className="sku-cell">
                                              <div className="table-content-bold">
                                                <MotifTruncate
                                                  maxLines={1}
                                                  maxWidth={200}
                                                >
                                                  {skuRelation.SkuName}
                                                </MotifTruncate>
                                              </div>
                                              <div className="sku-number">
                                                <span>{skuRelation.SkuId}</span>

                                                <MotifTooltip
                                                  contentClassName="tooltip-content"
                                                  placement="top"
                                                  hideCloseButton
                                                  flip
                                                  offset={[0, 8]}
                                                  allowHover
                                                  allowClick={true}
                                                  trigger={
                                                    <MotifChip
                                                      className={`sku-badge ${skuRelation.SkuType === "Fixed" ? "fixed" : skuRelation.SkuType === "Variable" ? "variable" : "extension"}`}
                                                      onClick={() => {}}
                                                      disabled={false}
                                                    >
                                                      {skuRelation?.SkuType}
                                                    </MotifChip>
                                                  }
                                                  variant="default"
                                                >
                                                  <div className="label-info-icon-text">
                                                    {skuRelation?.SkuType ==
                                                    "Fixed"
                                                      ? fixedCostInfo()
                                                      : skuRelation.SkuType ===
                                                          "Variable"
                                                        ? variableCostInfo()
                                                        : extensionCostInfo()}
                                                  </div>
                                                </MotifTooltip>
                                              </div>
                                            </div>
                                          </li>
                                        );
                                      },
                                    )}
                                  </ul>
                                </ul>
                              </td>
                              <td>
                                <div className="table-cell">
                                  <div className="table-content-bold">
                                    {sku?.EngagementId
                                      ? sku.EngagementId
                                      : "--"}
                                  </div>
                                  <div className="table-content-normal">
                                    {sku?.EngagementName ? (
                                      <MotifTruncate
                                        maxLines={1}
                                        maxWidth={120}
                                      >
                                        {sku?.EngagementName}
                                      </MotifTruncate>
                                    ) : (
                                      "--"
                                    )}
                                  </div>
                                </div>
                                {sku?.SkuRelation?.map(
                                  (skuRelation: any, index: number) => {
                                    return (
                                      <div key={index} className="table-cell">
                                        <div className="table-content-bold">
                                          {skuRelation?.EngagementId
                                            ? skuRelation.EngagementId
                                            : "--"}
                                        </div>
                                        <div className="table-content-normal">
                                          {skuRelation?.EngagementName ? (
                                            <MotifTruncate
                                              maxLines={1}
                                              maxWidth={120}
                                            >
                                              {skuRelation?.EngagementName}
                                            </MotifTruncate>
                                          ) : (
                                            "--"
                                          )}
                                        </div>
                                      </div>
                                    );
                                  },
                                )}
                              </td>
                              <td>
                                <div className="table-cell">
                                  <div className="text-right-align table-content-bold">
                                    {sku?.CostMetricCost.toLocaleString(
                                      "en-US",
                                      {
                                        style: "currency",
                                        currency: "USD",
                                        maximumFractionDigits: 2,
                                        minimumFractionDigits: 2,
                                      },
                                    )}
                                  </div>
                                  <div className="table-content-normal text-right-align">
                                    {getCostIntervalForCostMetric(
                                      sku.CostIntervalCd,
                                      sku.CostMetricCd,
                                    )}
                                  </div>
                                </div>
                                {sku?.SkuRelation?.map(
                                  (skuRelation: any, index: number) => {
                                    return (
                                      <div key={index} className="table-cell">
                                        <div className="text-right-align table-content-bold">
                                          {skuRelation?.CostMetricCost.toLocaleString(
                                            "en-US",
                                            {
                                              style: "currency",
                                              currency: "USD",
                                              maximumFractionDigits: 2,
                                              minimumFractionDigits: 2,
                                            },
                                          )}
                                        </div>
                                        <div className="table-content-normal text-right-align">
                                          {getCostIntervalForCostMetric(
                                            skuRelation.CostIntervalCd,
                                            skuRelation.CostMetricCd,
                                          )}
                                        </div>
                                      </div>
                                    );
                                  },
                                )}
                              </td>
                              <td>
                                <div className="table-cell">
                                  <div className="table-content-bold">
                                    {sku?.OrderSkuQuantity?.toString() ?? ""}
                                  </div>
                                  <div className="table-content-normal">
                                    {getUnitsUsedForPeriod(
                                      sku.CostIntervalCd,
                                      sku.CostMetricCd,
                                      sku?.OrderSkuQuantity,
                                    )}
                                  </div>
                                </div>
                                {sku?.SkuRelation?.map(
                                  (skuRelation: any, index: number) => {
                                    return (
                                      <div key={index} className="table-cell">
                                        <div className="table-content-bold">
                                          {skuRelation?.OrderSkuQuantity?.toString() ??
                                            ""}
                                        </div>
                                        <div className="table-content-normal">
                                          {getUnitsUsedForPeriod(
                                            skuRelation.CostIntervalCd,
                                            skuRelation.CostMetricCd,
                                            skuRelation?.OrderSkuQuantity,
                                          )}
                                        </div>
                                      </div>
                                    );
                                  },
                                )}
                              </td>
                              <td>
                                <div className="table-cell">
                                  <div className="table-content-bold">
                                    {sku?.IsExtension
                                      ? sku.OrderSkuIntervals
                                      : 0}
                                  </div>
                                  <div className="table-content-normal">
                                    {getDuration(sku)}
                                  </div>
                                </div>
                                {sku?.SkuRelation?.map(
                                  (skuRelation: any, index: number) => {
                                    return (
                                      <>
                                        <div key={index} className="table-cell">
                                          <div className="table-content-bold">
                                            {skuRelation?.IsExtension
                                              ? skuRelation.OrderSkuIntervals
                                              : 0}
                                          </div>
                                          <div className="table-content-normal">
                                            {getDuration(skuRelation)}
                                          </div>
                                        </div>
                                      </>
                                    );
                                  },
                                )}
                              </td>
                              <td>
                                <div className="table-cell">
                                  <div className="text-right-align table-content-bold sub-total-cost">
                                    {sku?.IsExtension
                                      ? calculateTotalCost(sku)
                                      : zeroCostWithDecimal()}
                                  </div>
                                  <div className="text-right-align table-content-normal">
                                    {calculatePeriodTotalCost(sku) == "--"
                                      ? zeroCostWithDecimal()
                                      : calculatePeriodTotalCost(sku)}
                                    {`/${sku.CostIntervalCd}`}
                                  </div>
                                </div>
                                {sku?.SkuRelation?.map(
                                  (skuRelation: any, index: number) => {
                                    return (
                                      <div key={index} className="table-cell">
                                        <div className="text-right-align table-content-bold sub-total-cost">
                                          {skuRelation?.IsExtension
                                            ? calculateTotalCost(skuRelation)
                                            : zeroCostWithDecimal()}
                                        </div>
                                        <div className="text-right-align table-content-normal">
                                          {calculatePeriodTotalCost(
                                            skuRelation,
                                          ) == "--"
                                            ? zeroCostWithDecimal()
                                            : calculatePeriodTotalCost(
                                                skuRelation,
                                              )}
                                          {`/${skuRelation.CostIntervalCd}`}
                                        </div>
                                      </div>
                                    );
                                  },
                                )}
                              </td>
                            </tr>
                          );
                        }
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
